/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import {
  useReducer,
  useMemo,
  useContext,
  useCallback,
  createContext,
  type PropsWithChildren,
  type Dispatch,
} from 'react';
import { once } from 'lodash-es';
import { developmentMode } from '../utils/consts';
import {
  ModalWrapper,
  type ModalWrapperProps,
} from '../components/modal-elements';
import {
  Disable2FAAdminModal,
  Disable2FAModal,
  DisableSubscriptionModal,
  DisableSubscriptionAdminModal,
  Enable2FAModal,
  Login2FAModal,
  Reminder2FAModal,
  RemoveAccountModal,
  ResetPasswordModal,
  SendEmailsAdminModal,
  RebalanceUserAdminModal,
  RemoveUserKeysAdminModal,
  InitialPortfolioModal,
  KrakenInfoModal,
  BitvavoInfoModal,
  ExpectedPortfolioModal,
  UpdateServiceWorkerModal,
  NewRebalanceModal,
  type Disable2FAAdminModalData,
  type DisableSubscriptionModalData,
  type DisableSubscriptionAdminModalData,
  type RemoveUserKeysAdminModalData,
  type SendEmailsAdminModalData,
  type RebalanceUserAdminModalData,
  type KrakenInfoModalData,
  type BitvavoInfoModalData,
  type ExpectedPortfolioModalData,
  type NewRebalanceModalData,
  type Login2FAModalData,
} from '../components/modals';

const Modals = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { modalOpened, contentWrapperProps, Content } = state;
  const contextValue = useMemo(() => ({ ...state, dispatch }), [state]);
  const close = useCallback(() => dispatch({ type: 'closeModal' }), []);
  const ModalsContext = useMemo(() => createModalsContext(), []);

  return (
    <>
      <ModalsContext.Provider value={contextValue}>
        {children}
        <InModalContext.Provider value={true}>
          <ModalWrapper
            open={modalOpened}
            close={close}
            {...contentWrapperProps}
          >
            {!!Content && <Content />}
          </ModalWrapper>
        </InModalContext.Provider>
      </ModalsContext.Provider>
    </>
  );
};

type State<T extends Modal> = {
  openedModalName: T | null;
  modalOpened: boolean;
  contentWrapperProps: WithOptional<
    Omit<ModalWrapperProps, 'open' | 'onClose'>,
    'close'
  >;
  Content: (typeof modals)[T] | null;
  data: ModalsData[T] | undefined;
  queue: Array<{
    name: T;
    data?: ModalsData[T];
    contentWrapperProps?: WithOptional<
      Omit<ModalWrapperProps, 'open' | 'onClose'>,
      'close'
    >;
  }>;
};

const initialState = {
  openedModalName: null,
  modalOpened: false,
  contentWrapperProps: {
    modalName: '',
  },
  Content: null,
  data: undefined,
  queue: [],
};

const modals = {
  disable2FAAdmin: Disable2FAAdminModal,
  disable2FA: Disable2FAModal,
  disableSubscription: DisableSubscriptionModal,
  disableSubscriptionAdmin: DisableSubscriptionAdminModal,
  enable2FA: Enable2FAModal,
  login2FA: Login2FAModal,
  reminder2FA: Reminder2FAModal,
  removeAccount: RemoveAccountModal,
  resetPassword: ResetPasswordModal,
  sendEmailsAdmin: SendEmailsAdminModal,
  rebalanceUserAdmin: RebalanceUserAdminModal,
  removeUserKeysAdmin: RemoveUserKeysAdminModal,
  initialPortfolioModal: InitialPortfolioModal,
  krakenInfoModal: KrakenInfoModal,
  bitvavoInfoModal: BitvavoInfoModal,
  expectedPortfolio: ExpectedPortfolioModal,
  updateServiceWorker: UpdateServiceWorkerModal,
  newRebalance: NewRebalanceModal,
} as const;

export type Modal = keyof typeof modals;

type ModalsData = {
  disable2FAAdmin: Disable2FAAdminModalData;
  disable2FA: undefined;
  disableSubscription: DisableSubscriptionModalData;
  disableSubscriptionAdmin: DisableSubscriptionAdminModalData;
  enable2FA: undefined;
  login2FA: Login2FAModalData | undefined;
  reminder2FA: undefined;
  removeAccount: undefined;
  resetPassword: undefined;
  sendEmailsAdmin: SendEmailsAdminModalData;
  rebalanceUserAdmin: RebalanceUserAdminModalData;
  removeUserKeysAdmin: RemoveUserKeysAdminModalData;
  initialPortfolioModal: undefined;
  krakenInfoModal: KrakenInfoModalData;
  bitvavoInfoModal: BitvavoInfoModalData;
  expectedPortfolio: ExpectedPortfolioModalData;
  updateServiceWorker: undefined;
  newRebalance: NewRebalanceModalData;
};

type CloseModalAction = {
  type: 'closeModal';
};

type DismissModalsAction = {
  type: 'dismissModals';
  payload: { names: Modal[] };
};

type SetModalContentAction<T extends Modal> = {
  type: 'setModalContent';
  payload: {
    name: T;
    data?: ModalsData[T];
    prepend?: boolean;
  };
};

type SetWrapperPropsAction<T extends Modal> = {
  type: 'setWrapperProps';
  payload: State<T>['contentWrapperProps'];
};

type Action<T extends Modal> =
  | CloseModalAction
  | DismissModalsAction
  | SetModalContentAction<T>
  | SetWrapperPropsAction<T>;

const popNextFromQueue = <T extends Modal>(
  queue: State<T>['queue']
): State<T> => {
  if (queue.length === 0) return initialState;
  const [nextModal, ...remainingQueue] = queue;
  const Content = modals[nextModal.name];

  if (!Content) {
    throw new Error(`No such modal name: ${nextModal.name}`);
  }

  return {
    openedModalName: nextModal.name,
    modalOpened: true,
    Content,
    data: nextModal.data,
    contentWrapperProps: nextModal.contentWrapperProps || { modalName: '' },
    queue: remainingQueue,
  };
};

const reducer = <T extends Modal>(
  prevState: State<T>,
  action: Action<T>
): State<T> => {
  switch (action.type) {
    case 'closeModal':
      return popNextFromQueue(prevState.queue);
    case 'dismissModals': {
      const { names } = action.payload;
      if (developmentMode) {
        const unknown = names.filter((name) => !(name in modals));
        if (unknown.length > 0) {
          console.warn(
            `dismissModals: unknown modal name(s): ${unknown.join(', ')}`
          );
        }
      }
      const filteredQueue = prevState.queue.filter(
        (item) => !names.includes(item.name)
      );
      const activeMatches =
        !!prevState.openedModalName &&
        names.includes(prevState.openedModalName);

      if (!activeMatches) {
        return filteredQueue.length === prevState.queue.length
          ? prevState
          : { ...prevState, queue: filteredQueue };
      }

      return popNextFromQueue(filteredQueue);
    }
    case 'setWrapperProps':
      return {
        ...prevState,
        contentWrapperProps: action.payload,
      };
    case 'setModalContent': {
      const Content = modals[action.payload.name];

      if (!Content) {
        throw new Error(`No such modal name: ${action.payload.name}`);
      }

      // If a modal is already open, add the new modal to the queue
      if (prevState.modalOpened) {
        const newModalItem = {
          name: action.payload.name,
          data: 'data' in action.payload ? action.payload.data : undefined,
          contentWrapperProps: {
            modalName: action.payload.name,
          },
        };

        // If prepend is true, add to the beginning of the queue, otherwise add to the end
        const updatedQueue = action.payload.prepend
          ? [newModalItem, ...prevState.queue]
          : [...prevState.queue, newModalItem];

        return {
          ...prevState,
          queue: updatedQueue,
        };
      }

      // Otherwise, open the modal immediately
      return {
        ...prevState,
        openedModalName: action.payload.name,
        modalOpened: true,
        Content,
        data: 'data' in action.payload ? action.payload.data : undefined,
      };
    }
    default:
      return prevState;
  }
};

const createModalsContext = once(<T extends Modal>() =>
  createContext<
    State<T> & {
      dispatch: Dispatch<Action<T>>;
    }
  >({
    ...initialState,
    dispatch: () => undefined,
  })
);

const useModals = <T extends Modal>() => useContext(createModalsContext<T>());

const InModalContext = createContext<boolean>(false);
const useInModal = () => useContext(InModalContext);

export { Modals, useModals, useInModal };
